import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Package } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Client } from '@/types/client';
import { ProductCatalog, ClientProduct } from '@/types/clientProduct';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import api from '@/lib/api';

interface ProductsTabProps {
  client: Client | null;
}

interface ClientProductsResponse {
  data: ClientProduct[];
  config: {
    observations: string | null;
    valor_total: number | null;
    valor_mensalidade: number | null;
    valor_start: number | null;
    plano_contratado: string | null;
    quantidade_atendentes: number | null;
    quantidade_contatos: number | null;
  };
}

const ProductsTab: React.FC<ProductsTabProps> = ({ client }) => {
  const [clientProducts, setClientProducts] = useState<ClientProduct[]>([]);
  const [config, setConfig] = useState<{
    observations: string | null;
    valor_total: number | null;
    valor_mensalidade: number | null;
    valor_start: number | null;
    plano_contratado: string | null;
    quantidade_atendentes: number | null;
    quantidade_contatos: number | null;
  }>({ observations: null, valor_total: null, valor_mensalidade: null, valor_start: null, plano_contratado: null, quantidade_atendentes: null, quantidade_contatos: null });
  const [catalog, setCatalog] = useState<ProductCatalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [generalObservations, setGeneralObservations] = useState('');
  const [observationsDirty, setObservationsDirty] = useState(false);
  const [valorTotal, setValorTotal] = useState<string>('');
  const [valorMensalidade, setValorMensalidade] = useState<string>('');
  const [valorStart, setValorStart] = useState<string>('');
  const [planoContratado, setPlanoContratado] = useState<string>('');
  const [quantidadeAtendentes, setQuantidadeAtendentes] = useState<string>('');
  const [quantidadeContatos, setQuantidadeContatos] = useState<string>('');
  const [valoresDirty, setValoresDirty] = useState(false);

  useEffect(() => {
    if (client?.id) {
      loadClientProducts();
      loadCatalog();
    }
  }, [client?.id]);

  const loadClientProducts = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const result = await api.get<ClientProductsResponse>(
        `/client-products/client/${client.id}`
      );
      setClientProducts(result.data || []);
      setConfig(result.config || { observations: null, valor_total: null, valor_mensalidade: null, valor_start: null, plano_contratado: null, quantidade_atendentes: null, quantidade_contatos: null });
      setGeneralObservations(result.config?.observations ?? '');
      setValorTotal(formatNumberToBRL(result.config?.valor_total ?? undefined));
      setValorMensalidade(formatNumberToBRL(result.config?.valor_mensalidade ?? undefined));
      setValorStart(formatNumberToBRL(result.config?.valor_start ?? undefined));
      setPlanoContratado(result.config?.plano_contratado ?? '');
      setQuantidadeAtendentes(String(result.config?.quantidade_atendentes ?? ''));
      setQuantidadeContatos(String(result.config?.quantidade_contatos ?? ''));
      setObservationsDirty(false);
      setValoresDirty(false);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar produtos do cliente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const result = await api.get<{ data: ProductCatalog[] }>(
        '/client-products/catalog'
      );
      setCatalog(result.data || []);
    } catch (error) {
      console.error('Erro ao carregar catálogo:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar catálogo de produtos',
        variant: 'destructive',
      });
    }
  };

  const isLinked = (catalogId: number) =>
    clientProducts.some((p) => p.product_catalog_id === catalogId);

  const getClientProductByCatalogId = (catalogId: number) =>
    clientProducts.find((p) => p.product_catalog_id === catalogId);

  const handleToggleLink = async (catalogProduct: ProductCatalog) => {
    const linked = isLinked(catalogProduct.id);
    const cp = getClientProductByCatalogId(catalogProduct.id);

    if (linked && cp) {
      setSaving(true);
      try {
        await api.delete(`/client-products/${cp.id}`);
        toast({ title: 'Sucesso', description: 'Produto desvinculado' });
        loadClientProducts();
      } catch (error: any) {
        const msg = error?.response?.data?.error || 'Erro ao desvincular';
        toast({ title: 'Erro', description: msg, variant: 'destructive' });
      } finally {
        setSaving(false);
      }
    } else {
      setSaving(true);
      try {
        await api.post(`/client-products/client/${client!.id}`, {
          product_catalog_id: catalogProduct.id,
        });
        toast({ title: 'Sucesso', description: 'Produto vinculado' });
        loadClientProducts();
      } catch (error: any) {
        const msg = error?.response?.data?.error || 'Erro ao vincular';
        toast({ title: 'Erro', description: msg, variant: 'destructive' });
      } finally {
        setSaving(false);
      }
    }
  };

  const customProducts = clientProducts.filter((p) => !p.product_catalog_id);

  const handleAddNew = async () => {
    if (!client?.id || !newName.trim()) {
      toast({
        title: 'Campo obrigatório',
        description: 'Informe o nome do produto',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const catalogRes = await api.post<{ data: ProductCatalog }>(
        '/client-products/catalog',
        { name: newName.trim() }
      );
      const catalogItem = catalogRes.data;
      await api.post(`/client-products/client/${client.id}`, {
        product_catalog_id: catalogItem.id,
      });
      toast({ title: 'Sucesso', description: 'Produto cadastrado e vinculado' });
      setNewName('');
      loadClientProducts();
      loadCatalog();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Erro ao cadastrar produto';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const parseBRLToNumber = (s: string): number | null => {
    const clean = s.replace(/\D/g, '');
    if (!clean) return null;
    const n = parseInt(clean, 10) / 100;
    return isNaN(n) ? null : n;
  };

  const handleSaveConfig = async () => {
    if (!client?.id || (!observationsDirty && !valoresDirty)) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      if (observationsDirty) payload.observations = generalObservations.trim() || null;
      if (valoresDirty) {
        payload.valor_total = parseBRLToNumber(valorTotal);
        payload.valor_mensalidade = parseBRLToNumber(valorMensalidade);
        payload.valor_start = parseBRLToNumber(valorStart);
        payload.plano_contratado = planoContratado.trim() || null;
        payload.quantidade_atendentes = quantidadeAtendentes.trim() ? parseInt(quantidadeAtendentes, 10) : null;
        payload.quantidade_contatos = quantidadeContatos.trim() ? parseInt(quantidadeContatos, 10) : null;
      }
      await api.patch(`/client-products/client/${client.id}/config`, payload);
      setObservationsDirty(false);
      setValoresDirty(false);
      loadClientProducts();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Erro ao salvar configurações';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const formatNumberToBRL = (n: number | null | undefined): string => {
    if (n == null) return '';
    return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCurrencyInput = (v: string): string => {
    const n = v.replace(/\D/g, '');
    if (!n) return '';
    const i = parseInt(n, 10) / 100;
    return i.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleDelete = async (item: ClientProduct) => {
    if (!confirm(`Desvincular o produto "${item.name}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/client-products/${item.id}`);
      toast({ title: 'Sucesso', description: 'Produto desvinculado' });
      loadClientProducts();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Erro ao desvincular';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && clientProducts.length === 0) {
    return (
      <div className="space-y-4">
        <AtomicLoadingScreen
          message="Carregando produtos..."
          className="h-64"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5" />
            Produtos do catálogo
          </CardTitle>
          <p className="text-sm text-gray-500">
            Marque para vincular ao cliente. Produtos vinculados ficam indisponíveis para nova vinculação.
          </p>
        </CardHeader>
        <CardContent>
          {catalog.length === 0 ? (
            <p className="text-gray-500 text-sm">
              Catálogo vazio. Cadastre um novo produto abaixo.
            </p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {catalog.map((p) => {
                const linked = isLinked(p.id);
                return (
                  <li
                    key={p.id}
                    className={`flex items-center gap-3 py-2 px-3 rounded-lg border transition-colors ${
                      linked
                        ? 'border-primary-200 bg-primary-50/50'
                        : 'border-gray-200 bg-gray-50/30 hover:bg-gray-50'
                    }`}
                  >
                    <Checkbox
                      id={`prod-${p.id}`}
                      checked={linked}
                      onCheckedChange={() => handleToggleLink(p)}
                      disabled={saving}
                    />
                    <label
                      htmlFor={`prod-${p.id}`}
                      className={`flex-1 cursor-pointer select-none ${
                        linked ? 'font-medium text-primary-800' : 'text-gray-700'
                      }`}
                    >
                      {p.name}
                    </label>
                    {linked && (
                      <span className="text-xs text-primary-600 font-medium">
                        Vinculado
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {customProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Produtos específicos</CardTitle>
            <p className="text-sm text-gray-500">
              Produtos cadastrados apenas para este cliente
            </p>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {customProducts.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-200 bg-gray-50/50"
                >
                  <span className="font-medium">{item.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item)}
                    disabled={saving}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Plus className="w-5 h-5" />
            Cadastrar novo produto
          </CardTitle>
          <p className="text-sm text-gray-500">
            Adicionar produto ao catálogo e vincular ao cliente
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="flex-1 max-w-sm">
              <Label htmlFor="new-product" className="sr-only">
                Nome do produto
              </Label>
              <Input
                id="new-product"
                placeholder="Nome do produto"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
              />
            </div>
            <Button
              onClick={handleAddNew}
              disabled={saving || !newName.trim()}
              className="shrink-0"
            >
              Cadastrar e vincular
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5" />
            Observações
          </CardTitle>
          <p className="text-sm text-gray-500">
            Observações gerais sobre os produtos deste cliente
          </p>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Observações gerais..."
            value={generalObservations}
            onChange={(e) => {
              setGeneralObservations(e.target.value);
              setObservationsDirty(true);
            }}
            onBlur={handleSaveConfig}
            rows={4}
            className="resize-none"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label htmlFor="valor-total">Valor total (contrato)</Label>
              <Input
                id="valor-total"
                placeholder="0,00"
                value={valorTotal}
                onChange={(e) => { setValorTotal(formatCurrencyInput(e.target.value)); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="valor-mensalidade">Mensalidade</Label>
              <Input
                id="valor-mensalidade"
                placeholder="0,00"
                value={valorMensalidade}
                onChange={(e) => { setValorMensalidade(formatCurrencyInput(e.target.value)); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="valor-start">Taxa de Implantação / Setup</Label>
              <Input
                id="valor-start"
                placeholder="0,00"
                value={valorStart}
                onChange={(e) => { setValorStart(formatCurrencyInput(e.target.value)); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="plano-contratado">Plano contratado</Label>
              <Input
                id="plano-contratado"
                placeholder="ex: Plano 15-2k"
                value={planoContratado}
                onChange={(e) => { setPlanoContratado(e.target.value); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="quantidade-atendentes">Atendentes</Label>
              <Input
                id="quantidade-atendentes"
                type="number"
                min="1"
                placeholder="1"
                value={quantidadeAtendentes}
                onChange={(e) => { setQuantidadeAtendentes(e.target.value); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="quantidade-contatos">Contatos (mil)</Label>
              <Input
                id="quantidade-contatos"
                type="number"
                min="1"
                placeholder="1"
                value={quantidadeContatos}
                onChange={(e) => { setQuantidadeContatos(e.target.value); setValoresDirty(true); }}
                onBlur={handleSaveConfig}
                className="mt-1"
              />
            </div>
          </div>
          {(observationsDirty || valoresDirty) && (
            <p className="text-xs text-gray-500 mt-1">
              Alterações salvas ao sair do campo
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductsTab;
